/**
 * Created by vsuthar on 9/10/21
 * File: AddTenant
 * Project: Cloud Management Module ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Form, FormLayout, Select, Input, SubmitButton, RadioGroup, Textarea } from '@panwds/react-form'
import { Button, LoadingPage, Body, Bold } from '@panwds/react-ui'
import Page from '../../../common/components/page/Page'
import { BUTTON_CANCEL } from '../../../common/constants/LabelConstants'
import {
  BUTTON_ADD_TENANT,
  BUTTON_SAVE,
  FORM_FIELD_SUPPORT_CONTACT,
  FORM_FIELD_SUPPORT_CONTACT_ERROR_MESSAGE_NAME_255_MORE,
  FORM_FIELD_SUPPORT_CONTACT_ERROR_MESSAGE_NAME_REQUIRED,
  FORM_FIELD_SUPPORT_CONTACT_FROM_PARENT,
  FORM_FIELD_SUPPORT_CONTACT_INHERITANCE,
  FORM_FIELD_SUPPORT_CONTACT_USE_CUSTOM,
  FORM_FIELD_TENANT_ERROR_MESSAGE_NAME_255_MORE,
  FORM_FIELD_TENANT_ERROR_MESSAGE_NAME_REQUIRED,
  FORM_FIELD_TENANT_ERROR_NAME_ALREADY_EXIST,
  FORM_FIELD_TENANT_NAME,
  FORM_FIELD_TENANT_VERTICAL,
  FORM_FIELD_TENANT_VERTICALS,
  LABEL_ADD_NEW_ROOT,
  LABEL_ADD_NEW_TENANT,
  LABEL_ADD_NEW_TENANT_TEMPLATE,
  LABEL_EDIT_TENANT,
  PLACEHOLDER_VERTICAL,
  SUPPORT_CONTACT_PLACEHOLDER,
  TENANCY_TENANT_NAME_PLACEHOLDER,
} from '../../constants/LabelConstants'
import { ROOT_NODE_ID, TENANT_ADD } from '../../constants/TenancyConstants'
import { useFormContext } from 'react-hook-form'
import StringTemplate from '../../../common/utils/StringTemplate'
import { getVarValue } from '../../../common/utils/Sparky'
import { FF_ALLOW_ROOT_CREATE, FF_SUPPORT_CONTACT_ENABLED } from '../../../common/utils/FeatureFlags'
import { OBJECT } from '../../../common/utils/Utils'

const allowRootCreate = getVarValue(FF_ALLOW_ROOT_CREATE)
const supportContactEnabled = getVarValue(FF_SUPPORT_CONTACT_ENABLED)

/**
 * This component going to use when user Add/Edit SG group
 */

/**
 *
 * @param isLoading
 * @param onCancel
 * @param form
 * @param actionType
 * @param formValues
 * @param onSubmit
 * @returns {JSX.Element}
 * @constructor
 */
const AddTenant = ({ isLoading, error, onCancel, actionType, formValues, onSubmit, selectedTreeNode }) => {
  /**
   *
   * @type {(function(): void)|*}
   */
  const handleSubmit = useCallback(({ display_name, vertical, support_contact }) => {
    const values = OBJECT.reduce({ display_name: display_name?.trim(), vertical: vertical?.value, support_contact })
    onSubmit(values)
  }, [onSubmit])

  const title = useMemo(() => {
    if (!allowRootCreate || !selectedTreeNode) {
      return LABEL_ADD_NEW_TENANT
    }
    return actionType === TENANT_ADD ?
      (selectedTreeNode?.id === ROOT_NODE_ID ? LABEL_ADD_NEW_ROOT : StringTemplate(LABEL_ADD_NEW_TENANT_TEMPLATE, [selectedTreeNode?.display_name])) : LABEL_EDIT_TENANT
  }, [actionType, selectedTreeNode])

  return (
    <Page pageHeaderProps={{ title, size: 'xs', level: 2 }} addClassName='cm-p-3'>
      {isLoading ? <LoadingPage /> : <div className={'cm-mt-5'} style={{ minWidth: '25vw', width: '25%' }}>
        <Body addClassName={'cm-mb-5'}><Bold>ENTER SOME BASIC INFO</Bold></Body>
        {error && <Bold appearance='semibold' addClassName='cm-text-error'>{error}</Bold>}
        <Form style={{ width: '50%' }} onSubmit={handleSubmit} mode='onBlur' formProps={{ autoComplete: 'off' }} >
          <FormLayout>
            <FormInput formValues={formValues} selectedTreeNode={selectedTreeNode} actionType={actionType} />
          </FormLayout>
          <Footer {...{ onCancel, actionType }} />
        </Form>
      </div>
      }
    </Page>
  )
}

const Footer = ({ onCancel, actionType }) => {

  const { formState, getValues } = useFormContext()
  const { isValid } = formState

  const display_name = getValues('display_name')

  const disabled = useMemo(() => {
    return !isValid || !Boolean(display_name)
  }, [display_name, isValid])

  return (
    <div className={'cm-flex cm-gap-2 cm-flex-row cm-flex-1 cm-justify-end cm-pt-3'}>
      <Button onClick={onCancel}>{BUTTON_CANCEL}</Button>
      <SubmitButton
        appearance={'primary'}
        type={'submit'}
        disabled={disabled}
      >{actionType === TENANT_ADD ? BUTTON_ADD_TENANT : BUTTON_SAVE}</SubmitButton>
    </div>
  )
}

export const FormInput = ({ formValues, selectedTreeNode, actionType, loading = false }) => {
  const {
    setValue,
    trigger,
    clearErrors
  } = useFormContext()
  const [contactInheritance, setContactInheritance] = useState(formValues?.support_contact?.initial)

  useEffect(() => {
    setContactInheritance(formValues?.support_contact?.initial)
  }, [actionType, formValues?.support_contact?.initial])

  const tenantExist = useCallback((value) => {
    if (value?.trim().length === 0) {
      return FORM_FIELD_TENANT_ERROR_MESSAGE_NAME_REQUIRED
    }
    if (selectedTreeNode?.children && selectedTreeNode?.children?.length > 0) {
      const matchingArray = selectedTreeNode?.children?.filter((elem) => {
        return elem?.display_name.trim().toLowerCase() === value?.trim().toLowerCase()
      })
      if (matchingArray?.length > 0)
        return FORM_FIELD_TENANT_ERROR_NAME_ALREADY_EXIST
    }
  }, [selectedTreeNode])

  const canInherit = useMemo(() => {
    if (selectedTreeNode?.id === ROOT_NODE_ID) {
      return false
    }
    return !(selectedTreeNode?.parent_id === ROOT_NODE_ID && actionType !== TENANT_ADD)
  }, [actionType, selectedTreeNode])

  const onSupportContactInheritanceChange = useCallback((event) => {
    setValue('support_contact', formValues.support_contact[event.currentTarget.value])
    setContactInheritance(event.currentTarget.value)
  }, [formValues, setValue])

  const verticals = useMemo(() => {
    const items = Array.from(FORM_FIELD_TENANT_VERTICALS).sort().concat('Other')
    return [{ value: PLACEHOLDER_VERTICAL }, ...(items.map((value) => ({ value })))]
  }, [])

  useEffect(() => {
    clearErrors()
    setValue('display_name', formValues?.display_name, { shouldDirty: true })
    const vertical = OBJECT.hasObject(formValues?.vertical) && !!formValues?.vertical?.value ? formValues.vertical : verticals[0]
    if (vertical.value === PLACEHOLDER_VERTICAL) {
      setValue('vertical', null, { shouldDirty: true })
    }
    else {
      setValue('vertical', vertical, { shouldDirty: true })
    }
    if (supportContactEnabled) {
      if (selectedTreeNode?.id !== ROOT_NODE_ID) {
        setValue('support_contact_inheritance', formValues?.support_contact?.initial, { shouldDirty: true })
        setValue('support_contact', formValues?.support_contact[formValues?.support_contact?.initial], { shouldDirty: true })
        setContactInheritance(formValues?.support_contact?.initial)
      }
      else {
        setValue('support_contact_inheritance', 'custom', { shouldDirty: true })
        setValue('support_contact', '', { shouldDirty: true })
        setContactInheritance('custom')
      }
    }
  }, [formValues, setValue, clearErrors, verticals, selectedTreeNode])

  return <div>
    <Input
      required
      register={{
        minLength: { value: 1, message: FORM_FIELD_TENANT_ERROR_MESSAGE_NAME_REQUIRED },
        maxLength: { value: 255, message: FORM_FIELD_TENANT_ERROR_MESSAGE_NAME_255_MORE },
        validate: tenantExist,
        setValueAs: value => value.trim(),
        onChange: async () => trigger('display_name')
      }}
      disabled={loading}
      name={'display_name'}
      label={FORM_FIELD_TENANT_NAME}
      placeholder={TENANCY_TENANT_NAME_PLACEHOLDER}
    />
    <Select
      addClassName='cmm-modal-select'
      fitToItemWidth
      placeholder={PLACEHOLDER_VERTICAL}
      label={FORM_FIELD_TENANT_VERTICAL}
      name="vertical"
      items={verticals}
      disabled={loading}
      register={{
        required: false,
        onChange: event => {
          const vertical = event.target.value
          if (vertical.value === PLACEHOLDER_VERTICAL) {
            setValue('vertical', null)
          }
        }
      }}
    />
    {supportContactEnabled && (
      <div>
        <div className='cm-flex cm-gap-4 cm-items-center' style={{ minHeight: 32 }}>
          <span className={`cm-font-lato cm-text-xs cm-space-y-1 ${selectedTreeNode?.id === ROOT_NODE_ID ? 'cm-mb-1' : ''}`.trim()}>{FORM_FIELD_SUPPORT_CONTACT}</span>
          {canInherit && (
            <RadioGroup
              defaultValue='parent'
              className='cm-flex cm-gap-2'
              label={FORM_FIELD_SUPPORT_CONTACT_INHERITANCE}
              showLabel={false}
              name="support_contact_inheritance"
              disabled={loading}
              items={[
                { value: 'parent', children: FORM_FIELD_SUPPORT_CONTACT_FROM_PARENT },
                { value: 'custom', children: FORM_FIELD_SUPPORT_CONTACT_USE_CUSTOM },
              ]}
              register={{
                onChange: onSupportContactInheritanceChange
              }}
            />
          )}
        </div>
        <Textarea
          rowsMax={3}
          name='support_contact'
          label={FORM_FIELD_SUPPORT_CONTACT}
          register={{
            minLength: { value: 1, message: FORM_FIELD_SUPPORT_CONTACT_ERROR_MESSAGE_NAME_REQUIRED },
            maxLength: { value: 255, message: FORM_FIELD_SUPPORT_CONTACT_ERROR_MESSAGE_NAME_255_MORE },
            required: false,
            onChange: async () => trigger('support_contact'),
            setValueAs: value => value.trim(),
          }}
          showLabel={false}
          placeholder={SUPPORT_CONTACT_PLACEHOLDER}
          disabled={loading || contactInheritance === 'parent'}
          readOnly={contactInheritance === 'parent'}
        />
      </div>
    )}
  </div>
}
AddTenant.propTypes = {}
export default AddTenant
